'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { supabase } from '@/lib/supabaseClient';

interface RelatedThread {
  id: string;
  title: string;
  reply_count: number;
  created_at: string;
}

interface FAQEntry {
  id: string;
  question: string;
  answer: string;
}

interface TopContributor {
  communityUserId: string;
  username: string;
  avatarUrl: string | null;
  score: number;
}

interface Props {
  templateName: string;
  templateId: string;
  categoryName?: string;
  isLoggedIn: boolean;
}

export default function AskCommunityWidget({ templateName, templateId, categoryName, isLoggedIn }: Props) {
  const router = useRouter();
  const [relatedThreads, setRelatedThreads] = useState<RelatedThread[]>([]);
  const [faqEntries, setFaqEntries] = useState<FAQEntry[]>([]);
  const [topContributors, setTopContributors] = useState<TopContributor[]>([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (isLoggedIn && expanded) {
      loadWidgetData();
    }
  }, [isLoggedIn, expanded, templateName]);

  const loadWidgetData = async () => {
    setLoading(true);
    try {
      // Related threads by template name keywords
      const keywords = templateName.toLowerCase().split(/\s+/).filter((w) => w.length > 2);
      const { data: threads } = await supabase
        .from('community_threads')
        .select('id, title, reply_count, created_at')
        .order('created_at', { ascending: false })
        .limit(100);

      if (threads) {
        const related = threads
          .filter((t) => keywords.some((kw) => t.title.toLowerCase().includes(kw)))
          .slice(0, 4);
        setRelatedThreads(related);
      }

      // FAQ entries related to template
      const { data: faqThreads } = await supabase
        .from('community_threads')
        .select('id, title, body')
        .ilike('title', `%${templateName.split(' ')[0]}%`)
        .eq('is_solved', true)
        .limit(3);

      if (faqThreads) {
        setFaqEntries(faqThreads.map((t) => ({
          id: t.id,
          question: t.title,
          answer: t.body?.slice(0, 150) + '...' || '',
        })));
      }

      // Top contributors for support category
      const { data: supportCat } = await supabase
        .from('community_categories')
        .select('id')
        .ilike('slug', '%support%')
        .single();

      if (supportCat) {
        const { data: repData } = await supabase
          .from('community_reputation')
          .select('user_id, total_score, community_users!inner(id, username, avatar_url)')
          .order('total_score', { ascending: false })
          .limit(3);

        if (repData) {
          setTopContributors((repData as any[]).map((r) => ({
            communityUserId: r.community_users?.id || '',
            username: r.community_users?.username || 'Unknown',
            avatarUrl: r.community_users?.avatar_url || null,
            score: r.total_score,
          })));
        }
      }
    } catch (err) {
      console.error('AskCommunityWidget error:', err);
    }
    setLoading(false);
  };

  const handleAskCommunity = () => {
    if (!isLoggedIn) {
      router.push('/sign-up-login');
      return;
    }
    // Navigate to community with template name pre-filled
    const encoded = encodeURIComponent(templateName);
    router.push(`/community/category/support-knowledge?prefill=${encoded}`);
  };

  return (
    <div className="rounded-2xl overflow-hidden" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
      <div
        className="px-5 py-4 flex items-center justify-between cursor-pointer"
        style={{ borderBottom: expanded ? '1px solid var(--border)' : 'none' }}
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-2">
          <Icon name="ChatBubbleLeftRightIcon" size={18} style={{ color: 'var(--accent)' }} />
          <h3 className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>Ask the Community</h3>
        </div>
        <Icon name={expanded ? 'ChevronUpIcon' : 'ChevronDownIcon'} size={16} style={{ color: 'var(--text-secondary)' }} />
      </div>

      {expanded && (
        <div className="p-5 space-y-5">
          {/* Ask button */}
          <button
            onClick={handleAskCommunity}
            className="w-full btn-primary flex items-center justify-center gap-2"
          >
            <Icon name="PlusIcon" size={16} />
            Ask about "{templateName}"
          </button>

          {!isLoggedIn ? (
            <div className="text-center py-4">
              <Icon name="LockClosedIcon" size={24} className="mx-auto mb-2" style={{ color: 'var(--text-muted)' }} />
              <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Sign in to see community discussions</p>
              <Link href="/sign-up-login" className="text-xs mt-2 inline-block" style={{ color: 'var(--accent)' }}>Sign In</Link>
            </div>
          ) : loading ? (
            <div className="space-y-2">
              {[1, 2].map((i) => <div key={i} className="h-8 rounded-lg animate-pulse" style={{ background: 'var(--bg-secondary)' }} />)}
            </div>
          ) : (
            <>
              {/* Related Threads */}
              {relatedThreads.length > 0 && (
                <div>
                  <p className="text-xs font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>Related Discussions</p>
                  <div className="space-y-1">
                    {relatedThreads.map((t) => (
                      <Link
                        key={t.id}
                        href={`/community/thread/${t.id}`}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg transition-colors hover:opacity-80"
                        style={{ background: 'var(--bg-secondary)' }}
                      >
                        <Icon name="ChatBubbleLeftIcon" size={12} style={{ color: 'var(--accent)', flexShrink: 0 }} />
                        <p className="text-xs truncate flex-1" style={{ color: 'var(--text-primary)' }}>{t.title}</p>
                        <span className="text-xs flex-shrink-0" style={{ color: 'var(--text-secondary)' }}>{t.reply_count}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* FAQ Entries */}
              {faqEntries.length > 0 && (
                <div>
                  <p className="text-xs font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>Solved Questions</p>
                  <div className="space-y-1">
                    {faqEntries.map((faq) => (
                      <Link
                        key={faq.id}
                        href={`/community/thread/${faq.id}`}
                        className="flex items-start gap-2 px-3 py-2 rounded-lg transition-colors hover:opacity-80"
                        style={{ background: 'var(--bg-secondary)' }}
                      >
                        <Icon name="CheckCircleIcon" size={12} style={{ color: '#10b981', flexShrink: 0, marginTop: 2 }} />
                        <p className="text-xs" style={{ color: 'var(--text-primary)' }}>{faq.question}</p>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Top Contributors */}
              {topContributors.length > 0 && (
                <div>
                  <p className="text-xs font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>Top Helpers</p>
                  <div className="flex gap-2">
                    {topContributors.map((c) => (
                      <Link
                        key={c.communityUserId}
                        href={`/community/profile/${c.communityUserId}`}
                        className="flex flex-col items-center gap-1"
                        title={c.username}
                      >
                        <div
                          className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold overflow-hidden"
                          style={{ background: 'var(--accent-light)', color: 'var(--accent)' }}
                        >
                          {c.avatarUrl ? (
                            <img src={c.avatarUrl} alt={c.username} className="w-full h-full object-cover" />
                          ) : (
                            c.username[0]?.toUpperCase()
                          )}
                        </div>
                        <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>{c.username.slice(0, 8)}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
